import { useEffect, useRef } from "react";
import s from "./MPArticleView.module.css";

const MPArticleView = ({ html }) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  // Проверяем, является ли это полным HTML документом
  const isFullHTMLDocument =
    html &&
    (html.trim().startsWith("<!DOCTYPE") ||
      html.includes("<html") ||
      html.includes("<body"));

  // Обработка навигации к закладке после загрузки
  useEffect(() => {
    if (!html || !containerRef.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const bookmarkId = urlParams.get("bookmark");

    if (bookmarkId && !iframeRef.current) {
      setTimeout(() => {
        const element = document.getElementById(bookmarkId);
        if (element) {
          const elementRect = element.getBoundingClientRect();
          const targetPosition = elementRect.top + window.pageYOffset;

          window.scrollTo({
            top: targetPosition,
            behavior: "smooth",
          });
        }
      }, 500);
    }
  }, [html]);

  // Навигация к закладке в iframe
  useEffect(() => {
    if (!isFullHTMLDocument || !iframeRef.current || !html) {
      return;
    }

    const iframe = iframeRef.current;

    const handleIframeLoad = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) return;

        const updateHeight = () => {
          const height = Math.max(
            iframeDoc.body?.scrollHeight || 0,
            iframeDoc.documentElement?.scrollHeight || 0
          );
          if (height) {
            iframe.style.height = `${height}px`;
          }
        };

        // Скрываем скролл внутри iframe
        iframe.style.overflow = "hidden";
        const styleId = "iframe-hide-scrollbar";
        if (!iframeDoc.getElementById(styleId)) {
          const style = iframeDoc.createElement("style");
          style.id = styleId;
          style.textContent = `
            html, body { overflow: visible !important; }
            body { scrollbar-width: none; -ms-overflow-style: none; }
            ::-webkit-scrollbar { display: none; }
            @media print {
              html, body { overflow: visible !important; }
            }
          `;
          (iframeDoc.head || iframeDoc.documentElement).appendChild(style);
        }

        // Прокручиваем к закладке внутри iframe
        const urlParams = new URLSearchParams(window.location.search);
        const bookmarkId = urlParams.get("bookmark");
        if (bookmarkId) {
          const element = iframeDoc.getElementById(bookmarkId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        // Добавляем lazy loading для изображений в iframe
        const images = iframeDoc.querySelectorAll("img:not([loading])");
        images.forEach((img) => {
          img.setAttribute("loading", "lazy");
        });

        // Подгоняем высоту iframe под контент (с учётом поздней загрузки img/iframe)
        updateHeight();
        setTimeout(updateHeight, 100);
        setTimeout(updateHeight, 400);
        setTimeout(updateHeight, 1000);
        setTimeout(updateHeight, 2000);

        // При загрузке картинок/встроенных iframe — пересчитываем высоту
        iframeDoc.querySelectorAll("img, iframe").forEach((el) => {
          el.addEventListener?.("load", updateHeight, { once: true });
        });

        // Наблюдаем за изменениями размеров контента (если поддерживается)
        if (typeof iframe.contentWindow?.ResizeObserver === "function" && iframeDoc.body) {
          const ro = new iframe.contentWindow.ResizeObserver(() => updateHeight());
          ro.observe(iframeDoc.body);
        }
      } catch (e) {
        console.warn("Could not access iframe content:", e);
      }
    };

    iframe.addEventListener("load", handleIframeLoad);
    setTimeout(handleIframeLoad, 0);

    return () => {
      iframe.removeEventListener("load", handleIframeLoad);
    };
  }, [isFullHTMLDocument, html]);

  // Извлекаем стили из HTML
  const styleMatch = html?.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  const styles = styleMatch ? styleMatch.join("") : "";

  // Извлекаем title из HTML
  const titleMatch = html?.match(/<title[^>]*>([^<]+)<\/title>/i);
  const pageTitle = titleMatch ? titleMatch[1] : "Статья МУ";

  // Для частичного HTML контента извлекаем только body
  let bodyContent = html || "";
  const bodyMatch = html?.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    bodyContent = bodyMatch[1];
  } else if (isFullHTMLDocument) {
    bodyContent = html
      .replace(/<!DOCTYPE[^>]*>/gi, "")
      .replace(/<html[^>]*>/gi, "")
      .replace(/<\/html>/gi, "")
      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "");
  }

  return (
    <div className={s.container} ref={containerRef}>
      {styles && (
        <style
          dangerouslySetInnerHTML={{
            __html: styles.replace(/<style[^>]*>/gi, "").replace(/<\/style>/gi, ""),
          }}
        />
      )}
      <div
        className={s.htmlContent}
        dangerouslySetInnerHTML={{ __html: bodyContent }}
        role="article"
        aria-label={pageTitle}
      />
    </div>
  );
};

export default MPArticleView;
